import React from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  IconButton,
  Stack,
  Collapse,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate, useParams } from "react-router-dom";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { useDispatch } from "react-redux";
import { getFileSignedURLThunk as getAccessSignedURLThunk } from "@modules/stepper/redux/thunk/documentThunk";

import { getFileSignedURLThunk as getRowSignedURLThunk } from "@modules/stepper-row/redux/thunk/documentThunk";
import { RemoveRedEye } from "@mui/icons-material";

interface BankDocumentDetails {
  document_name: string;
  upload_path: string;
}

export interface BankGuarantee {
  bg_bank_name: string;
  bank_guarantee_no: string;
  bg_isssue_date: string;
  bg_validity_date: string;
  bg_claim_date: string;
  branch_address: string;
  bank_guarantee_document?: BankDocumentDetails;
}

interface Props {
  data?: BankGuarantee;
  isExpanded: boolean;
  toggleExpand: () => void;
}

const isValidBgDetails = (data?: BankGuarantee): boolean => {
  if (!data) return false;

  const requiredFields: (keyof BankGuarantee)[] = [
    "bg_bank_name",
    "bank_guarantee_no",
    "bg_isssue_date",
    "bg_validity_date",
    "bg_claim_date",
    "branch_address",
  ];

  return requiredFields.every(
    (key) =>
      data[key] !== null &&
      data[key] !== undefined &&
      String(data[key]).trim() !== "",
  );
};

/* -------- Date Formatter -------- */
const formatDate = (date?: string) => {
  if (!date) return "-";

  const d = new Date(date);
  if (isNaN(d.getTime())) return date;

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* -------- Check if Date is Expired -------- */
const isDateExpired = (date?: string): boolean => {
  if (!date) return false;

  const d = new Date(date);
  if (isNaN(d.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

export const BankGuaranteeDetails = ({
  data,
  isExpanded,
  toggleExpand,
}: Props) => {
  const dispatch = useDispatch();
  if (!data || !isValidBgDetails(data)) return null;

  const url = window.location.href;
  let row = url.includes("row");

  const handleViewFile = (
    document_name?: string,
    isRow?: boolean,
    dispatch?: any,
  ) => {
    if (!dispatch) return;

    return openFileBySignedUrl({
      document_name,
      dispatch,
      getSignedUrlThunk: isRow ? getRowSignedURLThunk : getAccessSignedURLThunk,
    });
  };

  const isValidityDateExpired = isDateExpired(data.bg_validity_date);

  const details = [
    { label: "Bank Name", value: data.bg_bank_name },
    { label: "electronic Bank Guarantee (e-BG) Number", value: data.bank_guarantee_no },
    {
      label: "electronic Bank Guarantee (e-BG) Issuance Date",
      value: formatDate(data.bg_isssue_date),
    },
    {
      label: "electronic Bank Guarantee (e-BG) Validity Date",
      value: formatDate(data.bg_validity_date),
      isExpired: isValidityDateExpired,
    },
    {
      label: "electronic Bank Guarantee (e-BG) Claim Date",
      value: formatDate(data.bg_claim_date),
    },
    { label: "Branch Address", value: data.branch_address },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #E7E6EB",
        borderRadius: "4px",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {/* Header strip */}
      <Box
        sx={{
          background: "#F4F2F7",
          pl: 2,
          pr: "2px",
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={toggleExpand}
      >
        <Typography
          fontSize={14}
          fontWeight={600}
          className="preview-dropdown-header"
        >
          electronic Bank Guarantee (e-BG) Details
        </Typography>

        <IconButton aria-label="Collapse section">
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {/* Expandable Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 3 }}>
          {/* Details Grid */}
          <Grid container rowSpacing={2.5} columnSpacing={6}>
            {details.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Typography
                  sx={{
                    color: "#8E8E93",
                    fontSize: 12,
                    mb: 0.5,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    color: item.isExpired ? "#D32F2F" : "#111",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {item.value || "-"}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Expiration Warning */}
          {isValidityDateExpired && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: "#FFEBEE",
                border: "1px solid #EF5350",
                borderRadius: "4px",
              }}
            >
              <Typography
                sx={{
                  color: "#C62828",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                The submitted Bank Guarantee (BG) has been expired. You are required to upload a valid Bank Guarantee for continuation of application processing.
              </Typography>
            </Box>
          )}

          {/* Document Section */}
          {data.bank_guarantee_document && (
            <Box sx={{ mt: 2 }}>
              <Typography fontSize={16} fontWeight={600} mb={1.5}>
                electronic Bank Guarantee (e-BG) Document
              </Typography>

              <Box
                sx={{
                  border: "1px solid #DADADA",
                  borderRadius: "6px",
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  maxWidth: 420,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PictureAsPdfIcon sx={{ color: "#E53935" }} />
                  <Typography
                    fontSize="0.85rem"
                    sx={{
                      textOverflow: "ellipsis",
                      maxWidth: 300,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                  >
                    {data.bank_guarantee_document.document_name}
                  </Typography>
                </Stack>

                <IconButton
                  // component="a"
                  // href={
                  //   process.env.REACT_APP_API_IMAGE_URL +
                  //   (row ? "noc-document/row/" : "noc-document/access/") +
                  //   data?.bank_guarantee_document?.upload_path
                  // }
                  // target="_blank"
                  // rel="noopener noreferrer"
                  // size="small"
                  onClick={() =>
                    handleViewFile(
                      data?.bank_guarantee_document?.upload_path
                        ?.split("/")
                        .pop(),
                      row,
                      dispatch,
                    )
                  }
                >
                  <RemoveRedEye fontSize="small" sx={{ color: "#757575" }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};
